import {
  forwardRef,
  useMemo,
  type ComponentPropsWithoutRef,
  type ElementType,
  type Ref,
} from "react";

import {
  sanitizeInlineHtml,
  sanitizeProductDescriptionHtml,
} from "~/utils/sanitize-html";

/**
 * Which sanitiser profile to apply.
 *
 * - `inline`  — short snippets (plan feature bullets, banner copy, modal
 *               one-liners). Inline emphasis only: no links, media or layout.
 * - `product` — rich supplier/AliExpress product descriptions. Allows block
 *               layout, tables and images, but never scripts, event handlers
 *               or unsafe URL schemes.
 */
export type SafeHtmlProfile = "inline" | "product";

type SafeHtmlOwnProps<T extends ElementType> = {
  /** Untrusted HTML. Always sanitised before it reaches the DOM. */
  html: string | null | undefined;
  profile?: SafeHtmlProfile;
  /** Render as a different element or styled-component. Defaults to `span`. */
  as?: T;
};

export type SafeHtmlProps<T extends ElementType> = SafeHtmlOwnProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof SafeHtmlOwnProps<T> | "children">;

/**
 * The single sanctioned `dangerouslySetInnerHTML` sink in this app.
 *
 * Every place that renders HTML from the API (product descriptions, plan
 * feature strings) must go through this component. `react/no-danger` is an
 * error everywhere else, so a new raw sink fails lint instead of shipping.
 */
function SafeHtmlInner<T extends ElementType = "span">(
  { html, profile = "inline", as, ...rest }: SafeHtmlProps<T>,
  ref: Ref<Element>
) {
  const Component = (as ?? "span") as ElementType;

  // Sanitising parses the whole string, which is expensive for rich supplier
  // descriptions. Callers such as the product modal re-render on every variant
  // or tab change, so key the result on the input rather than reparsing.
  const __html = useMemo(
    () =>
      profile === "product"
        ? sanitizeProductDescriptionHtml(html)
        : sanitizeInlineHtml(html),
    [html, profile]
  );

  return (
    <Component
      {...rest}
      ref={ref}
      // eslint-disable-next-line react/no-danger -- the one audited sink in the app; every input is sanitised above
      dangerouslySetInnerHTML={{ __html }}
    />
  );
}

export const SafeHtml = forwardRef(SafeHtmlInner) as <
  T extends ElementType = "span",
>(
  props: SafeHtmlProps<T> & { ref?: Ref<Element> }
) => JSX.Element;
